/**
 * Textbausteine **und Makros** verwalten (Spec 8.2 / 7.3).
 *
 * Beides gehoert zusammen: ein Makro darf einen Baustein in den Editor legen
 * (`insert_snippet`), und wer das eine pflegt, pflegt meist auch das andere.
 *
 * Der Geltungsbereich entscheidet ueber die Rechte: persoenliche Bausteine
 * darf jeder Agent pflegen, Mandanten- und Queue-Bausteine nur ein Admin.
 * Die API setzt das durch — hier wird die Auswahl nur entsprechend gefiltert.
 */
import {
  emptyTiptapDoc,
  renderTiptapText,
  SNIPPET_SCOPES,
  type Macro,
  type Queue,
  type Snippet,
  type SnippetScope,
  type Tag,
  type Team,
  type TenantMemberDetail,
  type TiptapDoc,
} from '@tickethub/shared';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MacroEditor } from '@/components/MacroEditor';
import { RichTextEditor } from '@/components/RichTextEditor';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { macrosApi, membersApi, queuesApi, snippetsApi, tagsApi, teamsApi } from '@/lib/tickets';

export function SnippetsPage() {
  const { t } = useTranslation();
  const { activeMembership } = useSession();
  const tenantId = activeMembership?.tenant_id ?? null;
  const isAdmin = activeMembership?.role === 'admin';

  const [snippets, setSnippets] = useState<Snippet[] | null>(null);
  const [queues, setQueues] = useState<Queue[]>([]);
  const [macros, setMacros] = useState<Macro[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [members, setMembers] = useState<TenantMemberDetail[]>([]);
  const [title, setTitle] = useState('');
  const [scope, setScope] = useState<SnippetScope>('personal');
  const [queueId, setQueueId] = useState('');
  const [body, setBody] = useState<TiptapDoc>(emptyTiptapDoc());
  const [saving, setSaving] = useState(false);

  const scopes = isAdmin ? SNIPPET_SCOPES : (['personal'] as const);

  async function reload() {
    if (!tenantId) return;
    try {
      const [snippetResult, macroResult] = await Promise.all([
        snippetsApi.list(tenantId),
        macrosApi.list(tenantId),
      ]);
      setSnippets(snippetResult.items);
      setMacros(macroResult.items);
    } catch (error) {
      setSnippets([]);
      toastError(error, t);
    }
  }

  useEffect(() => {
    if (!tenantId) return;
    void (async () => {
      try {
        const [snippetResult, queueResult, macroResult, tagResult, teamResult] = await Promise.all([
          snippetsApi.list(tenantId),
          queuesApi.list(tenantId),
          macrosApi.list(tenantId),
          tagsApi.list(tenantId),
          teamsApi.list(tenantId),
        ]);
        setSnippets(snippetResult.items);
        setQueues(queueResult.items);
        setMacros(macroResult.items);
        setTags(tagResult.items);
        setTeams(teamResult.items);
      } catch (error) {
        setSnippets([]);
        toastError(error, t);
      }

      // Die Mitgliederliste darf nur `member.manage` — ohne sie fehlt in der
      // Makro-Aktion „Zuweisen" nur die Personen-Auswahl.
      try {
        setMembers((await membersApi.list(tenantId)).items);
      } catch {
        setMembers([]);
      }
    })();
  }, [tenantId, t]);

  async function create() {
    if (!tenantId || title.trim().length === 0) return;
    setSaving(true);
    try {
      await snippetsApi.create(tenantId, {
        title: title.trim(),
        body_json: body,
        scope,
        queue_id: scope === 'queue' ? queueId : null,
      });
      setTitle('');
      setBody(emptyTiptapDoc());
      await reload();
    } catch (error) {
      toastError(error, t);
    } finally {
      setSaving(false);
    }
  }

  async function remove(snippetId: string) {
    if (!tenantId) return;
    try {
      await snippetsApi.remove(tenantId, snippetId);
      await reload();
    } catch (error) {
      toastError(error, t);
    }
  }

  if (!activeMembership) {
    return (
      <Alert>
        <AlertDescription>{t('tenant.empty')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('snippet.title')}</h1>

      <section className="space-y-3 rounded-lg border p-4">
        <h2 className="text-lg font-medium">{t('snippet.new')}</h2>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="snippet-title">{t('snippet.name')}</Label>
            <Input
              id="snippet-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="snippet-scope">{t('snippet.scope')}</Label>
            <Select
              id="snippet-scope"
              value={scope}
              onChange={(event) => setScope(event.target.value as SnippetScope)}
            >
              {scopes.map((value) => (
                <option key={value} value={value}>
                  {t(`snippet.scope_${value}`)}
                </option>
              ))}
            </Select>
          </div>

          {scope === 'queue' ? (
            <div className="space-y-2">
              <Label htmlFor="snippet-queue">{t('ticket.queue')}</Label>
              <Select
                id="snippet-queue"
                value={queueId}
                onChange={(event) => setQueueId(event.target.value)}
              >
                <option value="">{t('snippet.choose_queue')}</option>
                {queues.map((queue) => (
                  <option key={queue.id} value={queue.id}>
                    {queue.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
        </div>

        <RichTextEditor
          value={body}
          onChange={setBody}
          disabled={saving}
          ariaLabel={t('snippet.body')}
        />

        <Button
          disabled={saving || title.trim().length === 0 || renderTiptapText(body).trim().length === 0}
          onClick={() => void create()}
        >
          {saving ? t('common.loading') : t('snippet.create')}
        </Button>
      </section>

      {snippets === null ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : snippets.length === 0 ? (
        <Alert>
          <AlertDescription>{t('snippet.empty')}</AlertDescription>
        </Alert>
      ) : (
        <ul className="space-y-2">
          {snippets.map((snippet) => (
            <li key={snippet.id} className="flex items-start gap-3 rounded-lg border p-3">
              <div className="flex-1">
                <p className="font-medium">{snippet.title}</p>
                <p className="text-sm text-muted-foreground">
                  {renderTiptapText(snippet.body_json).slice(0, 160)}
                </p>
              </div>
              <Badge variant="outline">{t(`snippet.scope_${snippet.scope}`)}</Badge>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('snippet.delete', { title: snippet.title })}
                onClick={() => void remove(snippet.id)}
              >
                <Trash2 aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* ── Makros (Spec 8.2) ─────────────────────────────────────────────── */}
      <section className="space-y-3">
        <div>
          <h2 className="text-xl font-semibold">{t('macro.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('macro.intro')}</p>
        </div>

        <MacroEditor
          macros={macros}
          tags={tags}
          snippets={snippets ?? []}
          members={members}
          teams={teams}
          readOnly={!isAdmin}
          onCreate={async (input) => {
            if (!tenantId) return;
            try {
              await macrosApi.create(tenantId, input);
              await reload();
            } catch (error) {
              toastError(error, t);
            }
          }}
          onDelete={async (macroId) => {
            if (!tenantId) return;
            try {
              await macrosApi.remove(tenantId, macroId);
              await reload();
            } catch (error) {
              toastError(error, t);
            }
          }}
        />
      </section>
    </div>
  );
}
